'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Collapsible } from '../_components/Collapsible';
import { PieChart } from '../_components/PieChart';
import {
  clearIdentity,
  loadIdentity,
  shortAccountId,
  type Identity,
} from '../../lib/identity';
import { saveRawToken, loadRawToken, clearRawToken } from '../../lib/token-cache';
import { bundleGrantCc, TIERS } from '@solidpeer/shared/pricing';
import {
  ApiCallFailed,
  abandonPayment,
  createEndpointAlias,
  createPayment,
  deleteEndpointAlias,
  disconnectTelegram,
  getAccount,
  getActivity,
  getPayment,
  getUsage,
  getWebPushConfig,
  issueToken,
  linkTelegram,
  listEndpointAliases,
  listPayments,
  listTokens,
  revokeToken,
  testTelegram,
  updateNotifyPrefs,
  type AccountDto,
  type ActivityRow,
  type EndpointAliasDto,
  type IssuedToken,
  type OutcomeFilter,
  type PaymentMethod,
  type PaymentRequestDto,
  type SubscriptionTerm,
  type TierName,
  type TokenDto,
  type UsageDto,
  type UsageWindow,
} from '../../lib/api';

interface NotifyPrefs {
  email?:           string;
  telegram?:        string;
  browser_pushsub?: Record<string, unknown>;
}

interface TokenScope {
  allowed_systems:  string[];
  allowed_networks: string[];
  allowed_methods:  string[];
  allowed_origins:  string[];
  /** ISO-8601 timestamp; empty string = no expiry. */
  expires_at:       string;
  /** Per-token RPS cap. 0 / undefined = inherit gateway / tier default. */
  rps_cap:          number | null;
  /** One-time CC budget for this token. 0 / undefined = no per-token cap;
   *  token only constrained by account balance. */
  spend_budget_cc:  number | null;
}

const ALL_SYSTEMS  = ['bchn', 'fulcrum', 'chaingraph'] as const;
const ALL_NETWORKS = ['mainnet', 'chipnet', 'testnet4', 'regtest'] as const;

export default function DashboardPage() {
  const router = useRouter();
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [account, setAccount]   = useState<AccountDto | null>(null);
  const [tokens, setTokens]     = useState<TokenDto[] | null>(null);
  const [busy, setBusy]         = useState<string | null>(null);
  const [error, setError]       = useState<string | null>(null);
  const [issued, setIssued]     = useState<IssuedToken | null>(null);
  const [mintOpen, setMintOpen] = useState(false);
  // Centralised confirm-state for destructive actions. Native
  // window.confirm sits outside our visual language and the
  // recovery-key warning needs more than one line.
  const [pendingConfirm, setPendingConfirm] = useState<
    | { kind: 'revoke';  token_id: string }
    | { kind: 'signout' }
    | null
  >(null);
  const [selectedToken, setSelectedToken] = useState<string | null>(null);
  const [tokenFilter, setTokenFilter]     = useState<'active' | 'all'>('active');

  // Bootstrap: load identity from IndexedDB; redirect to /login if absent.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const id = await loadIdentity();
      if (cancelled) return;
      if (!id) { router.replace('/login'); return; }
      setIdentity(id);
      void Promise.all([refreshAccount(id), refreshTokens(id.account_id)]);
    })();
    return () => { cancelled = true; };
  }, [router]);

  async function refreshAccount(id: Identity) {
    try {
      setAccount(await getAccount(id));
    } catch (err) {
      setError(asMessage(err));
    }
  }

  async function refreshTokens(account_id: string) {
    try {
      const { tokens } = await listTokens(account_id);
      setTokens(tokens);
    } catch (err) {
      setError(asMessage(err));
    }
  }

  async function onIssueToken(scope: TokenScope) {
    if (!identity) return;
    setBusy('Issuing token…'); setError(null); setIssued(null);
    try {
      // optional_caps holds per-token rate / budget tunables. Server
      // accepts arbitrary JSONB here; we structure the keys to match
      // gateway/src/types.ts TokenCaps so they're enforced correctly
      // on the gateway side. Empty caps object stays null on the wire.
      const optional_caps: Record<string, number> = {};
      if (scope.rps_cap         && scope.rps_cap > 0)         optional_caps.rps_cap         = scope.rps_cap;
      if (scope.spend_budget_cc && scope.spend_budget_cc > 0) optional_caps.spend_budget_cc = scope.spend_budget_cc;
      const t = await issueToken(identity, {
        allowed_systems:  scope.allowed_systems,
        allowed_networks: scope.allowed_networks,
        ...(scope.allowed_methods.length  ? { allowed_methods:  scope.allowed_methods }  : {}),
        ...(scope.allowed_origins.length  ? { allowed_origins:  scope.allowed_origins }  : {}),
        ...(scope.expires_at              ? { expires_at:       scope.expires_at }       : {}),
        ...(Object.keys(optional_caps).length ? { optional_caps } : {}),
      });
      setIssued(t);
      setMintOpen(false);
      // Persist the raw token on this device so the TokenDetailPanel
      // can hydrate concrete URLs later (server only stored the hash).
      // Best-effort — IndexedDB unavailability falls through to the
      // `<your-token>` placeholder downstream.
      void saveRawToken(t.token_id, t.raw_token);
      await refreshTokens(identity.account_id);
    } catch (err) {
      setError(friendlyMintError(err));
    } finally {
      setBusy(null);
    }
  }

  function friendlyMintError(err: unknown): string {
    if (!(err instanceof ApiCallFailed)) return asMessage(err);
    const body = err.body as { error?: string; reason?: string };
    if (err.status === 403 && body.reason === 'requires_paid_tier') {
      return 'Token minting needs an active paid subscription. Pick a tier from /pricing first; tokens unlock the moment your first payment lands.';
    }
    return asMessage(err);
  }

  async function onRevoke(token_id: string) {
    if (!identity) return;
    setBusy(`Revoking ${token_id}…`); setError(null);
    try {
      await revokeToken(identity, token_id);
      // Drop the cached raw token — the URL we'd hydrate would be
      // a 401 anyway, and keeping it around is just a stale-secret
      // foot-gun.
      void clearRawToken(token_id);
      await refreshTokens(identity.account_id);
    } catch (err) {
      setError(asMessage(err));
    } finally {
      setBusy(null);
      setPendingConfirm(null);
    }
  }

  async function onSaveNotifyPrefs(prefs: NotifyPrefs | null) {
    if (!identity) return;
    setBusy('Saving notification preferences…'); setError(null);
    try {
      // Cast to the wider `Record<string, unknown>` the API client expects —
      // the typed NotifyPrefs is structurally compatible at runtime, just
      // missing the index signature TS wants.
      const updated = await updateNotifyPrefs(identity, prefs as Record<string, unknown> | null);
      setAccount(updated);
    } catch (err) {
      setError(asMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function onSignOut() {
    await clearIdentity();
    router.replace('/');
  }

  if (!identity) {
    return (
      <main className="container-x py-24 text-sm text-ink-dim text-center">
        Loading…
      </main>
    );
  }

  // Token table: filter by active/all + sort newest-first. Sorting in
  // render is fine here — N is bounded by tier caps (Hobby:4, Build:25,
  // Scale:200, Business:500); even at the cap this is microseconds.
  const visibleTokens = (tokens ?? [])
    .filter((t) => tokenFilter === 'all' || t.status === 'active')
    .slice()
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));

  return (
    <main className="container-x py-12 space-y-10">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="heading-2">Dashboard.</h1>
          <p className="mt-1 text-xs text-ink-faint font-mono break-all">
            {identity.account_id}
          </p>
        </div>
        <button onClick={() => setPendingConfirm({ kind: 'signout' })} className="btn-secondary text-sm">
          Sign out
        </button>
      </header>

      {error && (
        <div className="rounded-md bg-red-500/10 ring-1 ring-inset ring-red-500/30 p-4">
          <div className="text-sm font-medium text-red-300">Error</div>
          <p className="mt-1 text-xs text-red-200/80 break-words">{error}</p>
        </div>
      )}

      {issued && <IssuedTokenCard issued={issued} onDismiss={() => setIssued(null)} />}

      {/* -------------------------------------- First-run onboarding hint */}
      {/* Show only before the customer mints anything AND only when the
          server-side gate would actually let them mint. Trial accounts
          see the AccountStatusCard's "no payment yet" banner + the
          Tokens section's "Subscribe to mint" CTA instead. */}
      {tokens !== null && tokens.length === 0 && !issued && !mintOpen
       && account && account.tier !== 'trial' && account.first_payment_at !== null && (
        <FirstRunHint onMint={() => setMintOpen(true)} />
      )}

      {/* ---------------------------------------------------- Open payments */}
      {/* Banner-style — no collapsible chrome; only renders when there's
          something pending so it's already self-hiding. The internal
          listPayments() fetch fires on mount but is bounded (limit=5,
          one round-trip) and the banner is the most action-relevant
          surface for someone who just paid. */}
      <OpenPaymentsBanner identity={identity} />

      {/* --------------------------------------------------------- Status */}
      {/* Account status: open by default. The AccountStatusCard
          renders from the already-loaded `account` prop — no extra
          API call — so eager render here costs nothing. */}
      <Collapsible title="Account" defaultOpen
                   subtitle={account ? `${account.tier} · ${formatCc(BigInt(account.balance_cc))} CC` : 'Loading…'}>
        {() => <AccountStatusCard account={account} />}
      </Collapsible>

      {/* ---------------------------------------------------------- Top up */}
      <Collapsible title="Top up" subtitle="Add CC to your balance">
        {() => <TopUpSection account_id={identity.account_id} account={account} />}
      </Collapsible>

      {/* ---------------------------------------------------- Payment history */}
      <Collapsible title="Payment history" subtitle="Past quotes — paid and expired">
        {() => <PaymentHistorySection identity={identity} />}
      </Collapsible>

      {/* ----------------------------------------------------------- Endpoints */}
      <Collapsible title="Endpoints" subtitle="URL patterns + supported networks">
        {() => <EndpointsCard />}
      </Collapsible>

      {/* ---------------------------------------------------------- Usage */}
      <Collapsible title="Usage" subtitle="CC consumed, top methods, top tokens, outcome breakdown">
        {() => <UsageSection identity={identity} />}
      </Collapsible>

      {/* ---------------------------------------------------------- Activity */}
      <Collapsible title="Recent activity" subtitle="Per-request stream with outcome chip + filters">
        {() => <ActivitySection identity={identity} />}
      </Collapsible>

      {/* ---------------------------------------------------------- Tokens */}
      <Collapsible title="Tokens" defaultOpen
                   subtitle={tokens === null ? 'Loading…' :
                             tokens.length === 0 ? 'No tokens yet' :
                             `${tokens.filter(t => t.status === 'active').length} active${tokens.length > tokens.filter(t => t.status === 'active').length ? ` · ${tokens.length - tokens.filter(t => t.status === 'active').length} revoked` : ''}`}>
        {() => (
        <section>
          {(() => {
          // Token issuance is gated server-side by:
          //   - tier (403 + requires_paid_tier on trial)
          //   - active-token count (403 + token_count_limit at tier max_tokens)
          // Mirror both gates in the UI so the user sees why the button is
          // unavailable instead of clicking through to a generic 403.
          const tierDef           = account ? TIERS[account.tier as TierName] : null;
          const maxTokens         = tierDef?.max_tokens ?? null;
          const activeTokenCount  = tokens?.filter((t) => t.status === 'active' && !t.revoked_at).length ?? 0;
          const atTokenLimit      = maxTokens !== null && activeTokenCount >= maxTokens;
          const tierAllowsMint    = !!account && account.tier !== 'trial' && account.first_payment_at !== null;
          const canMint           = tierAllowsMint && !atTokenLimit;
          return (
            <>
              <div className="flex flex-wrap items-center justify-end gap-3 mb-4">
                <div className="flex items-center gap-2">
                  {tokens && tokens.length > 0 && (
                    <div className="flex gap-1">
                      <FilterPill active={tokenFilter === 'active'} onClick={() => setTokenFilter('active')}>
                        Active
                      </FilterPill>
                      <FilterPill active={tokenFilter === 'all'}    onClick={() => setTokenFilter('all')}>
                        All
                      </FilterPill>
                    </div>
                  )}
                  {tierAllowsMint && maxTokens !== null && (
                    <span className="text-xs text-ink-faint">
                      {activeTokenCount} / {maxTokens} active{atTokenLimit ? ' (at limit)' : ''}
                    </span>
                  )}
                  {canMint && !mintOpen && (
                    <button onClick={() => setMintOpen(true)}
                            disabled={busy !== null}
                            className="btn-primary text-sm disabled:opacity-50">
                      {busy ?? 'Mint a token'}
                    </button>
                  )}
                  {tierAllowsMint && atTokenLimit && (
                    <span className="text-xs text-ink-dim">
                      Revoke an existing token or <Link href="/pricing" className="text-accent hover:underline">upgrade your tier</Link> to mint more.
                    </span>
                  )}
                  {!tierAllowsMint && account && (
                    <Link href="/pricing" className="btn-primary text-sm">
                      Subscribe to mint tokens
                    </Link>
                  )}
                </div>
              </div>

              {canMint && mintOpen && (
                <TokenMintForm
                  busy={busy !== null}
                  account={account!}
                  onCancel={() => setMintOpen(false)}
                  onSubmit={onIssueToken}
                />
              )}
            </>
          );
        })()}

        {tokens === null && <p className="text-sm text-ink-dim">Loading tokens…</p>}

        {tokens && tokens.length === 0 && (
          <div className="card text-sm text-ink-dim">
            No tokens yet. Mint one — it'll work against the regtest endpoints immediately
            (top up to use mainnet once it's live).
          </div>
        )}

        {tokens && tokens.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-ink-faint">
                  <th className="py-3 pr-4 font-medium">ID</th>
                  <th className="py-3 px-4 font-medium">Status</th>
                  <th className="py-3 px-4 font-medium">Systems</th>
                  <th className="py-3 px-4 font-medium">Networks</th>
                  <th className="py-3 px-4 font-medium">Created</th>
                  <th className="py-3 pl-4 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {visibleTokens.map((t) => (
                  <tr key={t.token_id}
                      onClick={() => setSelectedToken(s => s === t.token_id ? null : t.token_id)}
                      className={`cursor-pointer hover:bg-surface-2/40 transition ${
                        selectedToken === t.token_id ? 'bg-surface-2/60' : ''
                      }`}>
                    <td className="py-3 pr-4 font-mono text-xs text-ink">{t.token_id}</td>
                    <td className="py-3 px-4">
                      {t.status === 'active'
                        ? <span className="pill bg-accent/15 text-accent">active</span>
                        : <span className="pill">revoked</span>}
                    </td>
                    <td className="py-3 px-4 text-xs text-ink-dim">{t.allowed_systems.join(', ')}</td>
                    <td className="py-3 px-4 text-xs text-ink-dim">{t.allowed_networks.join(', ')}</td>
                    <td className="py-3 px-4 text-xs text-ink-faint">
                      {new Date(t.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 pl-4 text-right">
                      {t.status === 'active' && (
                        <button onClick={(e) => { e.stopPropagation(); setPendingConfirm({ kind: 'revoke', token_id: t.token_id }); }}
                                disabled={busy !== null}
                                className="text-xs text-red-300 hover:text-red-200 disabled:opacity-50">
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selectedToken && (() => {
          const t = visibleTokens.find((x) => x.token_id === selectedToken);
          if (!t) return null;
          return (
            <TokenDetailPanel
              identity={identity}
              token={t}
              onClose={() => setSelectedToken(null)}
            />
          );
        })()}
      </section>
        )}
      </Collapsible>

      {/* ---------------------------------------------- Notify prefs editor */}
      <Collapsible title="Notifications" subtitle="Email / Telegram / browser-push">
        {() => (
          <NotifyPrefsCard
            identity={identity}
            account={account}
            prefs={account?.notify_prefs as NotifyPrefs | null | undefined}
            busy={busy !== null}
            onSave={onSaveNotifyPrefs}
            onAccountRefresh={() => { void getAccount(identity).then(setAccount).catch(() => {}); }}
          />
        )}
      </Collapsible>

      {/* ----------------------------------------------- Recovery-key card */}
      <Collapsible title="Recovery key" subtitle="WIF backup of the device privkey">
        {() => <RecoveryKeyCard identity={identity} />}
      </Collapsible>

      {pendingConfirm?.kind === 'revoke' && (
        <ConfirmDialog
          tone="danger"
          title="Revoke this token?"
          body={
            <>
              <p>
                Anything still using <code className="font-mono text-xs">{pendingConfirm.token_id}</code>
                {' '}will start getting{' '}
                <span className="font-mono">401 token_revoked</span> on its next call.
                Revocation is permanent — you can't un-revoke; mint a new token instead.
              </p>
            </>
          }
          confirmLabel="Revoke"
          cancelLabel="Cancel"
          busy={busy !== null}
          onConfirm={() => onRevoke(pendingConfirm.token_id)}
          onCancel={() => setPendingConfirm(null)}
        />
      )}

      {pendingConfirm?.kind === 'signout' && (
        <ConfirmDialog
          tone="danger"
          title="Sign out of this device?"
          body={
            <>
              <p>
                Sign-out wipes the privkey from this browser. If you haven't backed up
                your <span className="font-mono">WIF</span>, you'll lose access to this
                account permanently — no email-recovery, no support hand-off.
              </p>
              <p className="mt-3 text-ink-dim">
                If you're not sure, cancel and use the <span className="text-ink">Recovery key</span>
                {' '}card below to download a backup first.
              </p>
            </>
          }
          confirmLabel="Sign out"
          cancelLabel="Cancel"
          busy={false}
          onConfirm={async () => { await onSignOut(); setPendingConfirm(null); }}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </main>
  );
}

// ============================================================
// Token detail (per-token usage + activity)
// ============================================================

function TokenDetailPanel({ identity, token, onClose }: {
  identity: Identity; token: TokenDto; onClose: () => void;
}) {
  const [usage,    setUsage]    = useState<UsageDto | null>(null);
  const [recent,   setRecent]   = useState<ActivityRow[] | null>(null);
  const [err,      setErr]      = useState<string | null>(null);
  // Hydrate the raw token from the per-device IndexedDB cache. Null
  // means we never saved it (issued on a different device, browser
  // storage cleared, etc.) — endpoints render with a `<your-token>`
  // placeholder in that case.
  const [rawToken, setRawToken] = useState<string | null>(null);

  const token_id = token.token_id;

  useEffect(() => {
    let cancelled = false;
    setUsage(null); setRecent(null); setErr(null);
    Promise.all([
      getUsage(identity, { window: 'cycle', token_id }),
      getActivity(identity, { limit: 10, token_id }),
    ])
      .then(([u, a]) => { if (!cancelled) { setUsage(u); setRecent(a.rows); } })
      .catch((e) => { if (!cancelled) setErr(asMessage(e)); });
    void loadRawToken(token_id).then((r) => { if (!cancelled) setRawToken(r); });
    return () => { cancelled = true; };
  }, [identity, token_id]);

  // Edge URL for the gateway is the same origin the dashboard runs on
  // (single-domain edge-LB). SSR-safe fallback for the first render.
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  // Cartesian product of (system, network) — every URL the customer
  // can call with this token. If we have the raw token cached on this
  // device (issued from this browser), we render the live URL; otherwise
  // the placeholder `<your-token>` for the customer to substitute.
  const tokenSeg = rawToken ?? '<your-token>';
  const endpoints = token.allowed_systems.flatMap((system) =>
    token.allowed_networks.map((network) => ({
      system, network,
      url: `${origin}/${system}/${network}/${tokenSeg}`,
    }))
  );

  return (
    <div className="mt-4 card ring-accent/30 space-y-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-accent">Token detail</div>
          <h3 className="mt-1 text-base font-mono">{token_id}</h3>
        </div>
        <button onClick={onClose} className="btn-secondary text-xs">Close</button>
      </div>

      {/* ---- Endpoints — the URLs this token can hit. */}
      <div>
        <div className="flex items-baseline justify-between mb-2">
          <h4 className="text-xs uppercase tracking-wider text-ink-dim">
            Endpoints ({endpoints.length})
          </h4>
          <span className="text-xs text-ink-faint">
            {rawToken
              ? <>Hydrated from this device's cached secret — copy and use as-is.</>
              : <>This token wasn't issued from this browser. Replace <code className="font-mono text-ink">&lt;your-token&gt;</code> with the raw secret you saved at issuance.</>}
          </span>
        </div>
        <div className="space-y-1.5">
          {endpoints.map((e) => (
            <div key={`${e.system}-${e.network}`} className="flex items-center gap-2">
              <span className="text-xs font-mono text-ink-faint w-32 shrink-0">
                {e.system}/{e.network}
              </span>
              <code className="flex-1 font-mono text-xs text-ink-dim break-all bg-surface-2 ring-1 ring-inset ring-white/5 rounded px-2 py-1.5">
                {e.url}
              </code>
              <CopyButton text={e.url} label="Copy" />
            </div>
          ))}
        </div>
        {endpoints.length > 0 && (
          <details className="mt-3">
            <summary className="text-xs text-ink-faint cursor-pointer hover:text-ink-dim select-none">
              Show curl example
            </summary>
            <pre className="mt-2 font-mono text-xs text-ink break-all whitespace-pre-wrap bg-surface-2 ring-1 ring-inset ring-white/5 rounded p-3">
{exampleCall(endpoints[0]!.system, endpoints[0]!.url)}
            </pre>
          </details>
        )}
        {token.allowed_methods && token.allowed_methods.length > 0 && (
          <div className="mt-3 text-xs text-ink-faint">
            <span className="text-ink-dim">Method allowlist:</span>{' '}
            <span className="font-mono text-ink-dim">{token.allowed_methods.join(', ')}</span>
          </div>
        )}
        {token.allowed_origins && token.allowed_origins.length > 0 && (
          <div className="mt-1 text-xs text-ink-faint">
            <span className="text-ink-dim">Origin allowlist:</span>{' '}
            <span className="font-mono text-ink-dim">{token.allowed_origins.join(', ')}</span>
          </div>
        )}
        {token.expires_at && (
          <div className="mt-1 text-xs text-ink-faint">
            <span className="text-ink-dim">Expires:</span>{' '}
            <span className="font-mono text-ink-dim">{new Date(token.expires_at).toLocaleString()}</span>
          </div>
        )}
        <TokenCapsSummary caps={token.optional_caps} spent_cc={token.spent_cc} />
      </div>

      {/* ---- Subdomain aliases for this token. */}
      <AliasesSection identity={identity} token={token} />

      {err && <div className="text-sm text-red-300">{err}</div>}
      {!err && (!usage || !recent) && <p className="text-sm text-ink-dim">Loading usage…</p>}

      {!err && usage && recent && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <Stat label="CC this cycle" value={formatCc(BigInt(usage.totals.cc_charged))} />
            <Stat label="Calls"         value={usage.totals.calls.toLocaleString()} />
            <Stat label="Cached"
                  value={`${pct(usage.totals.cached, usage.totals.calls)}%`} />
            <Stat label="Rejected"
                  value={`${pct(usage.totals.rejected, usage.totals.calls)}%`} />
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-wider text-ink-dim mb-2">
              Last {recent.length} requests
            </h4>
            {recent.length === 0 && (
              <p className="text-sm text-ink-faint">
                No requests yet for this token. Once it makes a call, the row will show up here
                and in the main activity log.
              </p>
            )}
            {recent.length > 0 && (
              <table className="w-full text-xs">
                <tbody>
                  {recent.map((r) => (
                    <tr key={r.id} className="border-t border-white/5">
                      <td className="py-1.5 text-ink-dim font-mono whitespace-nowrap">{fmtTime(r.ts)}</td>
                      <td className="py-1.5 font-mono text-ink break-all">
                        <span className="text-ink-faint">{r.system}/</span>{r.method}
                      </td>
                      <td className="py-1.5"><OutcomeChip outcome={r.outcome} /></td>
                      <td className="py-1.5 text-right text-ink-dim font-mono">{r.duration_ms}ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function TokenCapsSummary({
  caps,
  spent_cc,
}: {
  caps:      Record<string, unknown> | null | undefined;
  spent_cc?: string;
}) {
  // Render only the per-token caps actually set (rps_cap / spend_budget_cc /
  // burst_cap / max_concurrent_subs). Empty caps means "inherit account
  // defaults" — surface that as a single-line hint instead of a no-op block.
  const rps = typeof caps?.rps_cap         === 'number' ? caps.rps_cap         : null;
  const brs = typeof caps?.burst_cap       === 'number' ? caps.burst_cap       : null;
  const bud = typeof caps?.spend_budget_cc === 'number' ? caps.spend_budget_cc : null;
  const sub = typeof caps?.max_concurrent_subs === 'number' ? caps.max_concurrent_subs : null;
  const anySet = rps !== null || brs !== null || bud !== null || sub !== null;
  if (!anySet) {
    return (
      <div className="mt-3 text-xs text-ink-faint">
        <span className="text-ink-dim">Caps:</span> inherits your account-level tier defaults.
      </div>
    );
  }
  // Per-token cumulative spend. Only meaningful when bud is set (the
  // budget is the denominator). Show "X / Y CC used" with a thin
  // progress bar that turns amber at ≥90% — same color logic as the
  // account-level CcBurnProgress so the visual cue is consistent.
  const spent = spent_cc ? BigInt(spent_cc) : 0n;
  const showBudgetUsage = bud !== null && BigInt(bud) > 0n;
  const usedPct = showBudgetUsage
    ? Number((spent * 10000n) / BigInt(bud!)) / 100
    : 0;
  const usedPctClamped = Math.min(100, Math.max(0, usedPct));
  return (
    <div className="mt-3 space-y-0.5 text-xs text-ink-faint">
      <div><span className="text-ink-dim">Per-token caps:</span></div>
      {rps !== null && <div className="ml-3">RPS: <span className="font-mono text-ink-dim">{rps.toLocaleString()}</span></div>}
      {brs !== null && <div className="ml-3">Burst: <span className="font-mono text-ink-dim">{brs.toLocaleString()}</span></div>}
      {bud !== null && (
        <div className="ml-3">
          <div>
            CC budget: <span className="font-mono text-ink-dim">{formatCc(spent)}</span>
            {' / '}
            <span className="font-mono text-ink-dim">{formatCc(BigInt(bud))}</span>
            {' used '}
            <span className="text-ink-faint">({usedPctClamped.toFixed(0)}%)</span>
          </div>
          <div className="mt-1 h-1 w-48 rounded-full bg-surface-2 overflow-hidden">
            <div className={`h-full transition-all ${usedPctClamped >= 90 ? 'bg-amber-400/70' : 'bg-accent/60'}`}
                 style={{ width: `${usedPctClamped}%` }} />
          </div>
        </div>
      )}
      {sub !== null && <div className="ml-3">Concurrent subs: <span className="font-mono text-ink-dim">{sub.toLocaleString()}</span></div>}
    </div>
  );
}

// ============================================================
// Confirm dialog (destructive actions)
// ============================================================

function ConfirmDialog(props: {
  tone:         'danger' | 'neutral';
  title:        string;
  body:         React.ReactNode;
  confirmLabel: string;
  cancelLabel:  string;
  busy:         boolean;
  onConfirm:    () => void | Promise<void>;
  onCancel:     () => void;
}) {
  // Trap focus / dismiss-on-Escape so the modal behaves like a modal.
  // We don't lift a `<dialog>` element because Tailwind styling on
  // ::backdrop is finicky and the `dialog` element insists on being
  // appended at the top of the DOM, which breaks the existing layout.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !props.busy) props.onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [props]);

  const confirmCls = props.tone === 'danger'
    ? 'bg-red-500 text-white hover:bg-red-400 ring-1 ring-inset ring-red-400/40'
    : 'btn-primary';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={() => { if (!props.busy) props.onCancel(); }}
    >
      <div
        className="w-full max-w-md card ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold">{props.title}</h3>
        <div className="mt-3 text-sm text-ink-dim space-y-3">{props.body}</div>
        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <button onClick={props.onCancel}
                  disabled={props.busy}
                  className="btn-secondary text-sm disabled:opacity-50">
            {props.cancelLabel}
          </button>
          <button onClick={() => { void props.onConfirm(); }}
                  disabled={props.busy}
                  className={`text-sm font-medium px-4 py-2 rounded-md disabled:opacity-50 ${confirmCls}`}>
            {props.busy ? 'Working…' : props.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// First-run onboarding hint
// ============================================================

function FirstRunHint({ onMint }: { onMint: () => void }) {
  return (
    <section className="card ring-accent/30">
      <div className="text-xs uppercase tracking-wider text-accent">Welcome</div>
      <h3 className="mt-2 text-lg font-semibold">Mint your first token to start calling the gateway.</h3>
      <p className="mt-2 text-sm text-ink-dim">
        A token is the credential your code presents to a SolidPeer endpoint. Choose which
        systems and networks it can reach when you mint it; tighter scope means a smaller
        blast radius if it ever leaks. The reveal panel will give you a one-line curl you
        can paste-and-run.
      </p>
      <ol className="mt-4 space-y-1 text-sm text-ink-dim list-decimal list-inside">
        <li>Press <span className="text-ink">Mint a token</span>.</li>
        <li>Pick systems + networks. Per-method weights are at <span className="font-mono">/methods</span>.</li>
        <li>Copy the curl from the reveal panel and run it.</li>
      </ol>
      <div className="mt-5 flex flex-wrap gap-3">
        <button onClick={onMint} className="btn-primary text-sm">Mint a token</button>
        <Link href="/docs/billing" className="btn-secondary text-sm">How billing works</Link>
      </div>
    </section>
  );
}

// ============================================================
// Issued-token reveal card
// ============================================================

function IssuedTokenCard({ issued, onDismiss }: { issued: IssuedToken; onDismiss: () => void }) {
  // Pick the first allowed (system, network) for the example. The customer
  // probably wants to test with a network they actually selected; if they
  // chose multiple, the first is as good a default as any — they can swap
  // segments in the URL themselves.
  const system  = issued.allowed_systems[0]  ?? 'bchn';
  const network = issued.allowed_networks[0] ?? 'mainnet';
  // Edge URL for the gateway is the same origin the dashboard runs on
  // (single-domain edge-LB). SSR-safe fallback for the first render before
  // window is available — overwritten on client mount.
  const origin   = typeof window === 'undefined' ? '' : window.location.origin;
  const endpoint = `${origin}/${system}/${network}/${issued.raw_token}`;
  const example  = exampleCall(system, endpoint);

  return (
    <div className="card ring-accent/40 space-y-5">
      <div>
        <div className="text-xs uppercase tracking-wider text-accent">New token issued</div>
        <p className="mt-2 text-sm text-ink-dim">
          Save the raw token now — it's shown <strong>exactly once</strong>. The dashboard never sees it
          again after this card is dismissed. Treat it like a password.
        </p>
      </div>

      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wider text-ink-dim">Raw token</div>
        <div className="flex items-start gap-2">
          <code className="flex-1 font-mono text-xs text-ink break-all bg-surface-2 ring-1 ring-inset ring-white/5 rounded p-3">
            {issued.raw_token}
          </code>
          <CopyButton text={issued.raw_token} label="Copy" />
        </div>
        <p className="text-xs text-ink-faint font-mono">{issued.token_id}</p>
      </div>

      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wider text-ink-dim">Endpoint</div>
        <div className="flex items-start gap-2">
          <code className="flex-1 font-mono text-xs text-ink-dim break-all bg-surface-2 ring-1 ring-inset ring-white/5 rounded p-3">
            {endpoint}
          </code>
          <CopyButton text={endpoint} label="Copy" />
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wider text-ink-dim">Try it now</div>
        <div className="flex items-start gap-2">
          <pre className="flex-1 font-mono text-xs text-ink break-all whitespace-pre-wrap bg-surface-2 ring-1 ring-inset ring-white/5 rounded p-3">
{example}
          </pre>
          <CopyButton text={example} label="Copy" />
        </div>
        <p className="text-xs text-ink-faint">
          Pasting this into a shell on the same machine will hit the live gateway with the token
          above. Other systems / networks: swap the path segments to match any combo this token allows.
        </p>
      </div>

      <button onClick={onDismiss} className="btn-secondary text-sm">
        I've saved it
      </button>
    </div>
  );
}

/** Generate a sensible one-liner for `system`. BCHN/Chaingraph speak
 *  JSON-RPC POST; Fulcrum's HTTP transport accepts JSON-RPC POST too
 *  (the WebSocket path is for subscriptions and isn't a useful curl
 *  example). Keep payloads minimal — `getblockcount` is universal,
 *  no-arg, and exists on every system. */
function exampleCall(system: string, endpoint: string): string {
  const method = system === 'fulcrum' ? 'blockchain.headers.tip' : 'getblockcount';
  const body   = `{"jsonrpc":"2.0","id":1,"method":"${method}","params":[]}`;
  return [
    `curl -sS -X POST '${endpoint}' \\`,
    `  -H 'content-type: application/json' \\`,
    `  -d '${body}'`,
  ].join('\n');
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard write can fail on insecure contexts or stripped
      // permissions; fall back to selecting nothing — the user can
      // still copy from the visible code block.
    }
  }
  return (
    <button onClick={copy} className="btn-secondary text-xs whitespace-nowrap">
      {copied ? 'Copied' : label}
    </button>
  );
}

// ============================================================
// Status card
// ============================================================

function AccountStatusCard({ account }: { account: AccountDto | null }) {
  if (!account) return <SkeletonCard lines={5} label="Loading account" />;

  const balance = formatCc(BigInt(account.balance_cc));
  const cycle   = account.cycle_started_at && account.cycle_ends_at
    ? `${new Date(account.cycle_started_at).toLocaleDateString()} → ${new Date(account.cycle_ends_at).toLocaleDateString()}`
    : '—';
  const firstPaid = account.first_payment_at
    ? new Date(account.first_payment_at).toLocaleDateString()
    : 'never';

  return (
    <section className="card">
      <div className="flex justify-end">
        <Link href="/docs/billing" className="text-xs text-ink-faint hover:text-accent">
          How billing works →
        </Link>
      </div>

      {account.status === 'expired' && account.first_payment_at === null && (
        <div className="mt-4 rounded-md bg-yellow-500/10 ring-1 ring-inset ring-yellow-500/30 p-4 text-sm text-yellow-200">
          You haven't completed a payment yet. Token issuance and gateway access both require
          an active paid subscription — pick a tier from{' '}
          <Link href="/pricing" className="underline">pricing</Link>{' '}
          to start. Unfunded accounts are pruned 7 days after creation.
        </div>
      )}
      {account.status === 'suspended' && (
        <div className="mt-4 rounded-md bg-red-500/10 ring-1 ring-inset ring-red-500/30 p-4 text-sm text-red-200">
          Account suspended{account.suspended_reason ? `: ${account.suspended_reason}` : ''}.
          Contact support to lift.
        </div>
      )}

      <dl className="mt-6 grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-sm">
        <Stat label="Status">
          <span className={`pill ${account.status === 'active' ? 'bg-accent/15 text-accent' : ''}`}>
            {account.status}
          </span>
        </Stat>
        <Stat label="Tier" value={account.tier} />
        <Stat label="Term" value={account.subscription_term ?? '—'} />
        <Stat label="Balance">
          <span className="font-mono text-ink">{balance}</span>{' '}
          <span className="text-ink-faint">CC</span>
        </Stat>
        <Stat label="Cycle"        value={cycle} />
        <Stat label="First paid"   value={firstPaid} />
      </dl>

      <CycleProgress account={account} />
      <CcBurnProgress account={account} />
    </section>
  );
}

function CycleProgress({ account }: { account: AccountDto }) {
  // No cycle yet → no bar. Trial accounts and pre-payment accounts
  // both fall into this branch (cycle_* are null until first
  // billable cycle starts).
  if (!account.cycle_started_at || !account.cycle_ends_at) return null;

  const start = Date.parse(account.cycle_started_at);
  const end   = Date.parse(account.cycle_ends_at);
  const now   = Date.now();
  const total = end - start;
  if (!Number.isFinite(total) || total <= 0) return null;

  const pct_elapsed = Math.min(100, Math.max(0, ((now - start) / total) * 100));
  const days_left   = Math.max(0, Math.round((end - now) / 86400_000));

  // Renew CTA shows once we're inside the last 7 days, OR if cycle
  // already elapsed (paid tier doesn't auto-expire — customer can
  // still buy a fresh cycle). Trial tier has no renewal path.
  const renewable = account.tier !== 'trial' && account.subscription_term !== null;
  const showRenew = renewable && (days_left <= 7 || now >= end);

  return (
    <div className="mt-6">
      <div className="flex items-baseline justify-between text-xs text-ink-faint mb-2">
        <span>Cycle progress</span>
        <span>
          {pct_elapsed.toFixed(0)}% elapsed · {days_left} day{days_left === 1 ? '' : 's'} left
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-surface-2 overflow-hidden">
        <div className={`h-full transition-all ${days_left <= 3 ? 'bg-amber-400/70' : 'bg-accent/60'}`}
             style={{ width: `${pct_elapsed}%` }} />
      </div>
      {showRenew && <RenewButton account={account} />}
    </div>
  );
}

function CcBurnProgress({ account }: { account: AccountDto }) {
  // No cycle-grant-equivalent for trial / pre-payment accounts → no bar.
  // Trial uses the trial CC quota as the denominator (5M); other tiers
  // use the bundle grant for the active term. Pre-payment recurring
  // accounts (no subscription_term yet) get nothing — there's no
  // committed grant to compare against.
  const tier = account.tier as TierName;
  const term = account.subscription_term as SubscriptionTerm | null;
  let grant_cc: bigint;
  try {
    if (tier === 'trial')      grant_cc = BigInt(bundleGrantCc('trial', 'monthly'));
    else if (term)             grant_cc = BigInt(bundleGrantCc(tier, term));
    else                       return null;
  } catch {
    // Unknown tier (e.g. server has a tier the bundle helper doesn't recognize)
    return null;
  }
  if (grant_cc <= 0n) return null;

  const balance_cc = BigInt(account.balance_cc);
  // Burned = grant - balance, clamped to [0, grant]. Top-ups can push
  // balance > grant — surface as 0% burned (a positive surprise) rather
  // than a negative figure that confuses the bar.
  const burned_cc = balance_cc >= grant_cc ? 0n
                  : balance_cc <= 0n     ? grant_cc
                  : grant_cc - balance_cc;
  const pct_burned = Number((burned_cc * 10000n) / grant_cc) / 100;
  const balance_fmt = formatCc(balance_cc < 0n ? 0n : balance_cc);
  const grant_fmt   = formatCc(grant_cc);

  return (
    <div className="mt-4">
      <div className="flex items-baseline justify-between text-xs text-ink-faint mb-2">
        <span>CC burn</span>
        <span>
          {pct_burned.toFixed(0)}% used · {balance_fmt} / {grant_fmt} CC left
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-surface-2 overflow-hidden">
        <div className={`h-full transition-all ${pct_burned >= 90 ? 'bg-amber-400/70' : 'bg-accent/60'}`}
             style={{ width: `${pct_burned}%` }} />
      </div>
    </div>
  );
}

function RenewButton({ account }: { account: AccountDto }) {
  const router          = useRouter();
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState<string | null>(null);

  async function renew() {
    setBusy(true); setErr(null);
    try {
      // AccountDto.tier is `string` (a stringified union from the wire);
      // cast back to the strict TierName / SubscriptionTerm shapes that
      // createPayment expects. Server validates the value either way.
      const tier = account.tier              as TierName;
      const term = account.subscription_term as SubscriptionTerm | null;
      if (term === null) { setErr('no subscription term on file'); return; }
      const pr = await createPayment({
        account_id:     account.account_id,
        purpose:        'renewal',
        target_tier:    tier,
        target_term:    term,
        payment_method: 'bch',
      });
      if ('payment_request_id' in pr) {
        router.push(`/payment-request/${pr.payment_request_id}`);
      } else {
        // PaymentNoOpDto — server says no payment required (rare, but
        // possible if balance is somehow above the bundle price).
        setErr(pr.message ?? 'no payment required');
      }
    } catch (e) {
      console.error('renewal create failed', e);
      setErr(e instanceof ApiCallFailed ? e.message : 'failed to start renewal');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 flex items-center gap-3">
      <button onClick={renew} disabled={busy}
              className="btn-primary text-xs">
        {busy ? 'Starting…' : `Renew ${account.tier} (${account.subscription_term})`}
      </button>
      {err && <span className="text-xs text-red-400">{err}</span>}
    </div>
  );
}

// ============================================================
// Top-up flow (POST /payment-request → BCH/PUSD/MUSD deposit)
// ============================================================

const TOPUP_PRESETS = [10, 25, 50, 100, 250] as const;
const PAYMENT_METHODS: { value: PaymentMethod; label: string; hint: string }[] = [
  { value: 'bch',  label: 'BCH',  hint: 'Native, fastest confirmation' },
  { value: 'pusd', label: 'PUSD', hint: 'ParyonUSD CashToken — price-stable' },
  { value: 'musd', label: 'MUSD', hint: 'Moria USD CashToken — price-stable' },
];

type TopUpStep =
  | { kind: 'idle' }
  | { kind: 'submitting' }       // briefly while POST /payment-request is in-flight; on success we redirect
  | { kind: 'noop'; message: string }
  | { kind: 'error'; message: string };

// ============================================================
// Open payments banner — surfaces unpaid quotes so a customer who
// closed the deposit card by accident can resume the same quote
// instead of generating a new one (which burns a derivation index).
// ============================================================

function OpenPaymentsBanner({ identity }: { identity: Identity }) {
  const [open, setOpen] = useState<PaymentRequestDto[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    listPayments(identity, { status: 'open', limit: 5 })
      .then((r) => { if (!cancelled) setOpen(r.payments); })
      .catch((e) => { console.error('[OpenPaymentsBanner] listPayments caught:', e); });
    return () => { cancelled = true; };
  }, [identity]);

  if (!open || open.length === 0) return null;

  return (
    <section className="rounded-xl bg-amber-500/5 ring-1 ring-inset ring-amber-500/30 p-5 space-y-3">
      <div className="text-xs uppercase tracking-wider text-amber-300">
        {open.length} payment quote{open.length === 1 ? '' : 's'} awaiting your deposit
      </div>
      <ul className="space-y-2">
        {open.map((p) => (
          <li key={p.payment_request_id} className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <span className="text-ink">
              ${p.amount_usd}{' '}
              <span className="text-ink-faint">→</span>{' '}
              <span className="font-mono">{(p.payment_method ?? '—').toUpperCase()}</span>{' '}
              <span className="text-ink-faint">·</span>{' '}
              <span className="text-ink-dim">{p.purpose}{p.target_tier ? ` ${p.target_tier}` : ''}</span>
              {p.status === 'partial' && (
                <span className="ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-mono bg-amber-500/20 text-amber-200">
                  partial — top up to close
                </span>
              )}
            </span>
            <span className="text-xs text-ink-faint">
              expires {new Date(p.expires_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
            </span>
            <Link href={`/payment-request/${p.payment_request_id}`} className="btn-secondary text-xs">
              {p.status === 'partial' ? 'Continue deposit →' : 'View deposit →'}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ============================================================
// Endpoints reference card — URL patterns per system, always
// visible. Customers shouldn't have to mint a fresh token to be
// reminded what the URL shape looks like.
// ============================================================

const ENDPOINT_ROWS = [
  {
    system:    'bchn',
    label:     'BCHN JSON-RPC',
    transport: 'HTTPS POST',
    note:      'Bitcoin Cash node — read the chain, broadcast transactions.',
  },
  {
    system:    'fulcrum',
    label:     'Fulcrum (Electrum)',
    transport: 'HTTPS POST + WSS',
    note:      'Address subscriptions, scripthash history. WS upgrade on the same URL.',
  },
  {
    system:    'chaingraph',
    label:     'Chaingraph (GraphQL)',
    transport: 'HTTPS POST + WSS',
    note:      'Indexed query layer; subscriptions over WS.',
  },
] as const;

function EndpointsCard() {
  // Use the page's own origin so the URL the customer sees is
  // exactly what their requests will hit. SSR-safe fallback for
  // the first render — replaced on client mount.
  const [origin, setOrigin] = useState<string>('https://api.solidpeer.io');
  useEffect(() => {
    if (typeof window !== 'undefined') setOrigin(window.location.origin);
  }, []);

  return (
    <section className="card">
      <div className="flex justify-end">
        <Link href="/methods" className="text-xs text-ink-faint hover:text-accent">
          Per-method weights →
        </Link>
      </div>
      <p className="mt-2 text-sm text-ink-dim">
        Plug your token into the URL. Networks: <span className="font-mono text-ink">mainnet</span>,
        {' '}<span className="font-mono text-ink">chipnet</span>,
        {' '}<span className="font-mono text-ink">testnet4</span>,
        {' '}<span className="font-mono text-ink">regtest</span>. Per-method CC weights
        live at <Link href="/methods" className="hover:text-accent underline">/methods</Link>.
      </p>
      <div className="mt-5 space-y-3">
        {ENDPOINT_ROWS.map((r) => {
          const url = `${origin}/${r.system}/<network>/<token>`;
          return (
            <div key={r.system} className="bg-surface-2 ring-1 ring-inset ring-white/5 rounded p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-semibold text-ink">{r.label}</span>
                <span className="text-xs text-ink-faint font-mono">{r.transport}</span>
              </div>
              <div className="mt-2 flex items-start gap-2">
                <code className="flex-1 font-mono text-xs text-ink break-all">{url}</code>
                <CopyButton text={url} label="Copy" />
              </div>
              <p className="mt-2 text-xs text-ink-faint">{r.note}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ============================================================
// Payment history — past quotes (paid + expired) for accounting
// ============================================================

function PaymentHistorySection({ identity }: { identity: Identity }) {
  const [payments, setPayments] = useState<PaymentRequestDto[] | null>(null);
  const [filter, setFilter]     = useState<'all' | 'paid' | 'expired'>('all');
  const [err, setErr]           = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPayments(null); setErr(null);
    const status_arg = filter === 'all' ? 'all' : filter;
    listPayments(identity, { status: status_arg, limit: 50 })
      .then((r) => { if (!cancelled) setPayments(r.payments.filter(p => p.status !== 'pending')); })
      .catch((e) => { if (!cancelled) setErr(asMessage(e)); });
    return () => { cancelled = true; };
  }, [identity, filter]);

  return (
    <section>
      <div className="flex flex-wrap items-center justify-end gap-3 mb-4">
        <div className="flex gap-1">
          <FilterPill active={filter === 'all'}     onClick={() => setFilter('all')}>     All     </FilterPill>
          <FilterPill active={filter === 'paid'}    onClick={() => setFilter('paid')}>    Paid    </FilterPill>
          <FilterPill active={filter === 'expired'} onClick={() => setFilter('expired')}> Expired </FilterPill>
        </div>
      </div>

      {err && <div className="card text-sm text-red-300">{err}</div>}

      {!err && payments === null && (
        <SkeletonCard lines={4} label="Loading payment history" />
      )}

      {!err && payments && payments.length === 0 && (
        <div className="card text-sm text-ink-dim">
          No {filter === 'all' ? 'completed or expired' : filter} payments yet.
        </div>
      )}

      {!err && payments && payments.length > 0 && (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="text-left text-ink-faint">
              <tr className="border-b border-white/5">
                <th className="font-normal px-4 py-3">When</th>
                <th className="font-normal px-4 py-3">Purpose</th>
                <th className="font-normal px-4 py-3">Method</th>
                <th className="font-normal px-4 py-3 text-right">USD</th>
                <th className="font-normal px-4 py-3">Status</th>
                <th className="font-normal px-4 py-3">ID</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.payment_request_id} className="border-t border-white/5 align-top">
                  <td className="px-4 py-2 text-xs text-ink-dim font-mono whitespace-nowrap">
                    {new Date(p.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    <span className="text-ink-faint"> {new Date(p.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
                  </td>
                  <td className="px-4 py-2 text-xs text-ink">
                    {p.purpose}{p.target_tier ? ` ${p.target_tier}` : ''}{p.target_term ? `/${p.target_term}` : ''}
                  </td>
                  <td className="px-4 py-2 text-xs text-ink-dim font-mono">{(p.payment_method ?? '—').toUpperCase()}</td>
                  <td className="px-4 py-2 text-xs text-right text-ink font-mono">${p.amount_usd}</td>
                  <td className="px-4 py-2"><PaymentStatusChip status={p.status} /></td>
                  <td className="px-4 py-2 text-xs font-mono break-all">
                    <Link href={`/payment-request/${p.payment_request_id}`}
                          className="text-ink-faint hover:text-accent">
                      {p.payment_request_id.slice(0, 8)}… →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function PaymentStatusChip({ status }: { status: string }) {
  const tone = status === 'applied'                                        ? 'bg-emerald-500/15 text-emerald-300'
             : status === 'received_exact' || status === 'received_over'   ? 'bg-emerald-500/10 text-emerald-200'
             : status === 'partial'                                        ? 'bg-amber-500/20 text-amber-200'
             : status === 'pending'                                        ? 'bg-amber-500/15 text-amber-300'
             : status === 'expired' || status.startsWith('abandoned')      ? 'bg-ink-faint/15 text-ink-faint'
             :                                                               'bg-red-500/15 text-red-300';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-mono ${tone}`}>
      {status}
    </span>
  );
}

// ============================================================
// Top-up flow (POST /payment-request → BCH/PUSD/MUSD deposit)
// ============================================================

function TopUpSection({ account_id, account }: { account_id: string; account: AccountDto | null }) {
  const [open, setOpen]      = useState(false);
  const [amount, setAmount]  = useState<number>(25);
  const [custom, setCustom]  = useState('');
  const [method, setMethod]  = useState<PaymentMethod>('bch');
  const [step, setStep]      = useState<TopUpStep>({ kind: 'idle' });

  function pickPreset(v: number) { setAmount(v); setCustom(''); }
  function pickCustom(v: string)  {
    setCustom(v);
    const n = parseInt(v, 10);
    if (Number.isFinite(n) && n > 0) setAmount(n);
  }

  async function submit() {
    setStep({ kind: 'submitting' });
    try {
      const res = await createPayment({
        account_id,
        purpose:          'topup',
        target_topup_usd: amount,
        payment_method:   method,
      });
      if ('payment_request_id' in res) {
        // Redirect to the canonical /payment-request/<id> permalink so
        // the deposit lives at a shareable URL (countdown + QR + abandon
        // all already there). The TopUpSection itself just collects
        // amount + method.
        window.location.assign(`/payment-request/${res.payment_request_id}`);
        return;
      } else {
        setStep({ kind: 'noop', message: res.message });
      }
    } catch (err) {
      const msg = friendlyTopUpError(err);
      setStep({ kind: 'error', message: msg });
    }
  }

  function friendlyTopUpError(err: unknown): string {
    if (!(err instanceof ApiCallFailed)) return asMessage(err);
    // Server-side error vocabulary documented in BILLING.md / PAYMENTS.md.
    // Replace the raw 4xx/5xx body with a copy that explains the
    // path back to a working state.
    const body = err.body as { error?: string; field?: string; reason?: string };
    // Accept both the current `payments_not_enabled` and the legacy
    // `xpub_not_provisioned` reason during the rollover window.
    if (err.status === 503 &&
        (body.reason === 'payments_not_enabled' || body.reason === 'xpub_not_provisioned')) {
      return "Top-ups aren't enabled on this server yet.";
    }
    if (err.status === 503 && (body.reason === 'price_feed_unavailable' || (body.error ?? '').includes('price_feed'))) {
      return "FX price feed temporarily unavailable. Try again in a minute.";
    }
    if (err.status === 400 && (body.error ?? '').includes('hd_wallet_state singleton row missing')) {
      // Pre-fix legacy 400 path; should be 503 after the quote.ts
      // sentinel match, but kept for resilience to a stale upstream.
      return "Top-ups aren't enabled on this server yet.";
    }
    if (err.status === 400 && body.field === 'account.tier') {
      return "Top-up needs an active paid subscription. Pick a tier from the pricing page first; the topup endpoint only adds CC on top of a billed cycle.";
    }
    return asMessage(err);
  }

  return (
    <section className="card">
      <div className="flex justify-end">
        <Link href="/docs/payments" className="text-xs text-ink-faint hover:text-accent">
          How payments work →
        </Link>
      </div>

      {!open && step.kind === 'idle' && account?.tier === 'trial' && (
        <>
          <p className="mt-2 text-sm text-ink-dim">
            Top-up adds CC on top of a billed cycle. Trial accounts haven't picked a tier
            yet — start with a subscription, then come back here to top up between cycles
            if you burn through the included grant early.
          </p>
          <Link href="/pricing" className="btn-primary text-sm mt-4 inline-flex">
            See pricing
          </Link>
        </>
      )}

      {!open && step.kind === 'idle' && account && account.tier !== 'trial' && (
        <>
          <p className="mt-2 text-sm text-ink-dim">
            Add Compute Credits to your balance — pay in BCH or a price-stable CashToken
            (PUSD or MUSD). Credits never expire; the cycle window only resets the discount
            tier.
          </p>
          <button onClick={() => setOpen(true)} className="btn-primary text-sm mt-4">
            Top up CC
          </button>
        </>
      )}

      {!open && step.kind === 'idle' && !account && (
        <p className="mt-2 text-sm text-ink-dim">Loading account…</p>
      )}

      {open && step.kind !== 'noop' && (
        <div className="mt-4 space-y-5">
          <Field label="Amount (USD)"
                 hint="Quick presets, or type a custom amount. Minimum $1.">
            <div className="flex flex-wrap gap-2">
              {TOPUP_PRESETS.map((v) => (
                <button key={v} onClick={() => pickPreset(v)}
                        className={`px-3 py-1.5 text-sm rounded-md ring-1 ring-inset transition ${
                          amount === v && !custom
                            ? 'bg-accent/15 text-accent ring-accent/30'
                            : 'text-ink-dim ring-white/10 hover:bg-surface-2'
                        }`}>
                  ${v}
                </button>
              ))}
              <input
                type="number" min={1} placeholder="custom"
                value={custom}
                onChange={(e) => pickCustom(e.target.value)}
                className="w-24 rounded-md bg-surface-2 ring-1 ring-inset ring-white/5 px-3 py-1.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-accent/50"
              />
            </div>
          </Field>

          <Field label="Pay with">
            <div className="flex flex-wrap gap-2">
              {PAYMENT_METHODS.map((m) => (
                <button key={m.value} onClick={() => setMethod(m.value)}
                        title={m.hint}
                        className={`px-3 py-1.5 text-sm rounded-md ring-1 ring-inset transition ${
                          method === m.value
                            ? 'bg-accent/15 text-accent ring-accent/30'
                            : 'text-ink-dim ring-white/10 hover:bg-surface-2'
                        }`}>
                  {m.label}
                </button>
              ))}
            </div>
          </Field>

          {step.kind === 'error' && (
            <div className="rounded-md bg-red-500/10 ring-1 ring-inset ring-red-500/30 p-3 text-sm text-red-200">
              {step.message}
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <button onClick={submit}
                    disabled={step.kind === 'submitting' || amount < 1}
                    className="btn-primary text-sm disabled:opacity-50">
              {step.kind === 'submitting' ? 'Quoting…' : `Quote ${method.toUpperCase()} for $${amount}`}
            </button>
            <button onClick={() => { setOpen(false); setStep({ kind: 'idle' }); }}
                    disabled={step.kind === 'submitting'}
                    className="btn-secondary text-sm disabled:opacity-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {step.kind === 'noop' && (
        <div className="mt-4 rounded-md bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 p-3 text-sm text-emerald-200">
          {step.message}
        </div>
      )}
    </section>
  );
}


// ============================================================
// Endpoint aliases — per-token list/create/delete
// ============================================================

function AliasesSection({ identity, token }: { identity: Identity; token: TokenDto }) {
  const [aliases, setAliases] = useState<EndpointAliasDto[] | null>(null);
  const [busy,    setBusy]    = useState<'list' | 'create' | string | null>('list');
  const [err,     setErr]     = useState<string | null>(null);

  // Form state — slug is server-minted, customer only picks the
  // (system, network) and an optional display label.
  const [label,   setLabel]   = useState('');
  const [system,  setSystem]  = useState(token.allowed_systems[0] ?? 'chaingraph');
  const [network, setNetwork] = useState(token.allowed_networks[0] ?? 'mainnet');

  // Filter the account-wide list down to this token. The endpoint
  // returns ALL aliases for the account; per-token slicing is cheap
  // enough client-side (bounded by tier caps) to skip a dedicated
  // `?token_id=` filter.
  const refresh = () => {
    setBusy('list'); setErr(null);
    listEndpointAliases(identity)
      .then((r) => setAliases(r.aliases.filter((a) => a.token_id === token.token_id)))
      .catch((e) => setErr(asMessage(e)))
      .finally(() => setBusy(null));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token.token_id]);

  async function onCreate() {
    setBusy('create'); setErr(null);
    try {
      const created = await createEndpointAlias(identity, {
        token_id: token.token_id, system, network,
        display_label: label.trim() || null,
      });
      setAliases((prev) => [created, ...(prev ?? [])]);
      setLabel('');
    } catch (e) {
      const eb = e as { status?: number; body?: { reason?: string; error?: string } };
      if (eb.status === 409 && eb.body?.reason === 'token_endpoint_duplicate') {
        setErr(`This token already has an alias for ${system}/${network}.`);
      } else {
        setErr(eb.body?.error ?? asMessage(e));
      }
    } finally { setBusy(null); }
  }

  async function onDelete(s: string) {
    setBusy(s); setErr(null);
    try {
      await deleteEndpointAlias(identity, s);
      setAliases((prev) => prev?.filter((a) => a.slug !== s) ?? null);
    } catch (e) {
      setErr(asMessage(e));
    } finally { setBusy(null); }
  }

  // Networks/systems available on THIS token — restrict the form
  // pickers so a customer can't pick a combo the token doesn't
  // cover. Server enforces this too; client-side just keeps the
  // form clean.
  const sysChoices = token.allowed_systems;
  const netChoices = token.allowed_networks;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <h4 className="text-xs uppercase tracking-wider text-ink-dim">
          Subdomain aliases ({aliases?.length ?? '—'})
        </h4>
        <span className="text-xs text-ink-faint">
          Slug is server-minted (treat it like a password); set a label to identify each.
        </span>
      </div>

      {/* Existing aliases */}
      {aliases && aliases.length > 0 && (
        <ul className="space-y-2 mb-3">
          {aliases.map((a) => (
            <li key={a.slug} className="rounded-md ring-1 ring-inset ring-white/5 bg-surface-2/40 p-2 space-y-1">
              <div className="flex items-center gap-2 text-xs">
                <span className="font-mono text-ink-faint w-32 shrink-0">
                  {a.system}/{a.network}
                </span>
                <span className="text-ink truncate">
                  {a.display_label ?? <span className="text-ink-faint italic">unlabelled</span>}
                </span>
                <span className="ml-auto text-ink-faint whitespace-nowrap">
                  {a.last_used_at
                    ? <>used {fmtRelative(a.last_used_at)}</>
                    : <>not used yet</>}
                </span>
                <button
                  onClick={() => onDelete(a.slug)}
                  disabled={busy === a.slug}
                  className="text-red-300 hover:text-red-200 disabled:opacity-50">
                  {busy === a.slug ? 'Removing…' : 'Remove'}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-xs text-ink-dim break-all bg-surface-2 ring-1 ring-inset ring-white/5 rounded px-2 py-1.5">
                  {a.url}
                </code>
                <CopyButton text={a.url} label="Copy" />
              </div>
            </li>
          ))}
        </ul>
      )}
      {aliases && aliases.length === 0 && (
        <p className="text-xs text-ink-faint mb-3">No aliases for this token yet.</p>
      )}

      {/* Create form */}
      <div className="rounded-md ring-1 ring-inset ring-white/5 bg-surface-2/40 p-3 space-y-2">
        <div className="text-xs uppercase tracking-wider text-ink-faint">New alias</div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="optional label, e.g. production"
            maxLength={64}
            className="flex-1 min-w-[16ch] rounded-md bg-surface-2 ring-1 ring-inset ring-white/10 px-3 py-1.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-accent/50"
          />
          <select value={system} onChange={(e) => setSystem(e.target.value)}
                  className="rounded-md bg-surface-2 ring-1 ring-inset ring-white/10 px-2 py-1.5 text-sm">
            {sysChoices.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className="text-ink-faint">/</span>
          <select value={network} onChange={(e) => setNetwork(e.target.value)}
                  className="rounded-md bg-surface-2 ring-1 ring-inset ring-white/10 px-2 py-1.5 text-sm">
            {netChoices.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <button
            onClick={onCreate}
            disabled={busy !== null}
            className="btn-primary text-xs disabled:opacity-50">
            {busy === 'create' ? 'Creating…' : 'Create alias'}
          </button>
        </div>
        <p className="text-xs text-ink-faint">
          We'll mint a random 5-word slug like <code className="font-mono text-ink-dim">cobalt-marble-rebuild-falcon-pulley</code> —
          unguessable so attackers can't probe for working endpoints. The label is just for your dashboard.
        </p>
        {err && <div className="text-xs text-red-300">{err}</div>}
      </div>
    </div>
  );
}

/** Compact relative-time string for "last used 5m ago" markers. */
function fmtRelative(iso: string): string {
  const ms  = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return iso;
  const s = Math.round(ms / 1000);
  if (s < 60)        return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60)        return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48)        return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

// ============================================================
// Status card — Stat helper
// ============================================================

function Stat({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-ink-faint">{label}</dt>
      <dd className="mt-1 text-ink">{children ?? value ?? '—'}</dd>
    </div>
  );
}

// ============================================================
// Skeleton — shimmer placeholder for in-flight async sections
// ============================================================

function SkeletonCard({ lines = 3, label }: { lines?: number; label?: string }) {
  return (
    <div className="card animate-pulse" aria-busy="true" aria-label={label ?? 'Loading'}>
      <div className="h-4 bg-surface-2 rounded w-1/3 mb-4" />
      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className="h-3 bg-surface-2 rounded"
            style={{ width: `${85 - i * 8}%` }}
          />
        ))}
      </div>
    </div>
  );
}

function formatCc(n: bigint): string {
  // 1_500_000n → "1.5M". Keeps the column scannable when balances span
  // 5 OoM (Hobby 300M ↔ Business 20B).
  if (n >= 1_000_000_000n) return `${(Number(n) / 1e9).toFixed(2).replace(/\.?0+$/, '')}B`;
  if (n >= 1_000_000n)     return `${(Number(n) / 1e6).toFixed(2).replace(/\.?0+$/, '')}M`;
  if (n >= 1_000n)         return `${(Number(n) / 1e3).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toString();
}

// ============================================================
// Usage card
// ============================================================

const USAGE_WINDOWS: { value: UsageWindow; label: string }[] = [
  { value: 'cycle', label: 'Current cycle' },
  { value: '24h',   label: '24 hours'      },
  { value: '7d',    label: '7 days'        },
  { value: '30d',   label: '30 days'       },
];

function UsageSection({ identity }: { identity: Identity }) {
  const [window_, setWindow] = useState<UsageWindow>('cycle');
  const [usage, setUsage]    = useState<UsageDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]        = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    getUsage(identity, { window: window_ })
      .then((u) => { if (!cancelled) setUsage(u); })
      .catch((e) => { if (!cancelled) setErr(asMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [identity, window_]);

  // Silent refresh when the tab regains visibility — a customer who
  // left the dashboard open while making calls expects fresh totals on
  // tab-back. We deliberately don't toggle `loading`: that would flash
  // "Loading usage…" for what's usually a sub-second refetch and feel
  // worse than a stale-then-update.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      getUsage(identity, { window: window_ }).then(setUsage).catch(() => { /* keep last good */ });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [identity, window_]);

  return (
    <section>
      <div className="flex flex-wrap items-center justify-end gap-3 mb-4">
        <div className="flex gap-1">
          {USAGE_WINDOWS.map((w) => (
            <button
              key={w.value}
              onClick={() => setWindow(w.value)}
              className={`px-3 py-1 text-xs rounded-md ring-1 ring-inset transition ${
                window_ === w.value
                  ? 'bg-accent/15 text-accent ring-accent/30'
                  : 'text-ink-dim ring-white/10 hover:bg-surface-2'
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {err && (
        <div className="card text-sm text-red-300">{err}</div>
      )}

      {!err && (loading || !usage) && (
        <SkeletonCard lines={4} label="Loading usage" />
      )}

      {!err && usage && <UsageCard usage={usage} />}
    </section>
  );
}

function UsageCard({ usage }: { usage: UsageDto }) {
  const cc = BigInt(usage.totals.cc_charged);

  return (
    <div className="card space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="CC consumed" value={formatCc(cc)} />
        <Stat label="Calls" value={usage.totals.calls.toLocaleString()} />
        <Stat label="Cached" value={`${pct(usage.totals.cached, usage.totals.calls)}%`} />
        <Stat label="Rejected" value={`${pct(usage.totals.rejected, usage.totals.calls)}%`} />
      </div>

      <div className="text-xs text-ink-faint">
        {fmtDate(usage.from)} → {fmtDate(usage.to)}
        {' · '}
        {usage.totals.executed.toLocaleString()} executed
        {' · '}
        {usage.totals.errored.toLocaleString()} errored
      </div>

      {usage.top_methods.length === 0 && (
        <p className="text-sm text-ink-dim">
          No metered traffic in this window. Issue a token and start calling the gateway —
          billed requests show up here within seconds.
        </p>
      )}

      {usage.top_methods.length > 0 && (
        <>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xs uppercase tracking-wider text-ink-dim mb-3">CC by method</h3>
              <PieChart slices={pieSlices(usage.top_methods)} />
            </div>
            <div>
              <h3 className="text-xs uppercase tracking-wider text-ink-dim mb-3">Calls by outcome</h3>
              <PieChart slices={outcomeSlices(usage.totals)} />
            </div>
          </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <h3 className="text-xs uppercase tracking-wider text-ink-dim mb-2">Top methods</h3>
            <table className="w-full text-xs">
              <thead className="text-left text-ink-faint">
                <tr>
                  <th className="font-normal py-1">Method</th>
                  <th className="font-normal py-1">Net</th>
                  <th className="font-normal py-1 text-right">Calls</th>
                  <th className="font-normal py-1 text-right">CC</th>
                </tr>
              </thead>
              <tbody>
                {usage.top_methods.slice(0, 8).map((m, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="py-1.5 font-mono text-ink break-all">
                      <span className="text-ink-faint">{m.system}/</span>{m.method}
                    </td>
                    <td className="py-1.5 font-mono text-ink-dim">{m.network}</td>
                    <td className="py-1.5 text-right text-ink-dim">{m.calls.toLocaleString()}</td>
                    <td className="py-1.5 text-right text-ink font-mono">{formatCc(BigInt(m.cc_charged))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-wider text-ink-dim mb-2">Top tokens</h3>
            <table className="w-full text-xs">
              <thead className="text-left text-ink-faint">
                <tr>
                  <th className="font-normal py-1">Token</th>
                  <th className="font-normal py-1 text-right">Calls</th>
                  <th className="font-normal py-1 text-right">CC</th>
                </tr>
              </thead>
              <tbody>
                {usage.top_tokens.slice(0, 8).map((t, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="py-1.5 font-mono text-ink break-all">{t.token_id}</td>
                    <td className="py-1.5 text-right text-ink-dim">{t.calls.toLocaleString()}</td>
                    <td className="py-1.5 text-right text-ink font-mono">{formatCc(BigInt(t.cc_charged))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}
    </div>
  );
}

/** Build pie-chart slices from the four outcome counts on UsageDto.totals.
 *  Filtered to non-zero buckets so a clean account doesn't render four
 *  slivers next to a giant "executed" wedge. */
function outcomeSlices(totals: UsageDto['totals']): { label: string; value: number }[] {
  return [
    { label: 'executed', value: totals.executed },
    { label: 'cached',   value: totals.cached   },
    { label: 'rejected', value: totals.rejected },
    { label: 'errored',  value: totals.errored  },
  ].filter((s) => s.value > 0);
}

/** Build pie-chart slices from the UsageDto.top_methods array.
 *  Keeps the top 8 by cc_charged + folds the rest into an "other"
 *  bucket. The pie's PALETTE has 9 distinct colors so the grouping
 *  fits exactly. */
function pieSlices(top_methods: UsageDto['top_methods']): { label: string; value: number }[] {
  if (top_methods.length === 0) return [];
  const sorted = [...top_methods].sort((a, b) => Number(BigInt(b.cc_charged) - BigInt(a.cc_charged)));
  const top    = sorted.slice(0, 8);
  const tail   = sorted.slice(8);
  const slices = top.map((m) => ({
    label: `${m.system}/${m.method}`,
    value: Number(m.cc_charged),
  }));
  if (tail.length > 0) {
    const tail_sum = tail.reduce((s, m) => s + Number(m.cc_charged), 0);
    if (tail_sum > 0) slices.push({ label: `other (${tail.length})`, value: tail_sum });
  }
  return slices;
}

function pct(part: number, total: number): string {
  if (total === 0) return '0';
  return ((part / total) * 100).toFixed(part >= total ? 0 : 1).replace(/\.0$/, '');
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ============================================================
// Activity log
// ============================================================

const ACTIVITY_PAGE_SIZE = 50;

function ActivitySection({ identity }: { identity: Identity }) {
  const [rows, setRows]      = useState<ActivityRow[] | null>(null);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]        = useState<string | null>(null);
  const [filter, setFilter]  = useState<'all' | 'errors'>('all');

  // Filter is now server-side: the API takes ?outcome=errors and
  // returns only rejected:* / error:* rows from the entire account
  // history, not just whatever happens to be in the loaded buffer.
  // Translates the UI's two-bucket filter into the wire param.
  const wire_outcome: OutcomeFilter | undefined =
    filter === 'errors' ? 'errors' : undefined;

  // Initial load + reload when identity OR filter changes.
  useEffect(() => {
    let cancelled = false;
    setRows(null); setNextBefore(null); setErr(null); setLoading(true);
    getActivity(identity, { limit: ACTIVITY_PAGE_SIZE, outcome: wire_outcome })
      .then((p) => { if (!cancelled) { setRows(p.rows); setNextBefore(p.next_before); } })
      .catch((e) => { if (!cancelled) setErr(asMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [identity, wire_outcome]);

  // Silent refresh on tab-back, BUT only when the user is still on
  // page 1 — pulling a fresh page 1 while they've paginated would
  // either yank them back or merge inconsistent windows. Detect
  // "paginated" by buffer size: > one page means they clicked
  // Load more at least once.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if ((rows?.length ?? 0) > ACTIVITY_PAGE_SIZE) return;
      getActivity(identity, { limit: ACTIVITY_PAGE_SIZE, outcome: wire_outcome })
        .then((p) => { setRows(p.rows); setNextBefore(p.next_before); })
        .catch(() => { /* keep last good */ });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [identity, rows, wire_outcome]);

  async function loadMore() {
    if (!nextBefore) return;
    setLoading(true);
    try {
      const p = await getActivity(identity, { limit: ACTIVITY_PAGE_SIZE, before: nextBefore, outcome: wire_outcome });
      setRows((prev) => [...(prev ?? []), ...p.rows]);
      setNextBefore(p.next_before);
    } catch (e) {
      setErr(asMessage(e));
    } finally {
      setLoading(false);
    }
  }

  async function refresh() {
    setLoading(true); setErr(null);
    try {
      const p = await getActivity(identity, { limit: ACTIVITY_PAGE_SIZE, outcome: wire_outcome });
      setRows(p.rows); setNextBefore(p.next_before);
    } catch (e) {
      setErr(asMessage(e));
    } finally {
      setLoading(false);
    }
  }

  // Filter is now server-side, so `visible` is just the rows.
  const visible = rows;

  return (
    <section>
      <div className="flex flex-wrap items-center justify-end gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            <FilterPill active={filter === 'all'}    onClick={() => setFilter('all')}>All</FilterPill>
            <FilterPill active={filter === 'errors'} onClick={() => setFilter('errors')}>Errors only</FilterPill>
          </div>
          <button onClick={refresh} disabled={loading}
                  className="px-3 py-1 text-xs rounded-md ring-1 ring-inset ring-white/10 text-ink-dim hover:bg-surface-2 disabled:opacity-50">
            ↻ Refresh
          </button>
          <button onClick={() => downloadActivityCsv(rows ?? [])}
                  disabled={!rows || rows.length === 0}
                  title="Download the currently-loaded rows as CSV"
                  className="px-3 py-1 text-xs rounded-md ring-1 ring-inset ring-white/10 text-ink-dim hover:bg-surface-2 disabled:opacity-50">
            ⤓ CSV
          </button>
        </div>
      </div>

      {err && <div className="card text-sm text-red-300">{err}</div>}

      {!err && rows === null && (
        <SkeletonCard lines={6} label="Loading activity" />
      )}

      {!err && rows && rows.length === 0 && (
        <div className="card text-sm text-ink-dim">
          No requests yet. Once a token in this account starts hitting the gateway, calls
          will show up here in real time.
        </div>
      )}

      {!err && visible && visible.length > 0 && (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-xs">
            <thead className="text-left text-ink-faint">
              <tr className="border-b border-white/5">
                <th className="font-normal px-3 py-2">When</th>
                <th className="font-normal px-3 py-2">Method</th>
                <th className="font-normal px-3 py-2">Net</th>
                <th className="font-normal px-3 py-2">Outcome</th>
                <th className="font-normal px-3 py-2 text-right">CC</th>
                <th className="font-normal px-3 py-2 text-right">Duration</th>
                <th className="font-normal px-3 py-2">Token</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id} className="border-t border-white/5 align-top">
                  <td className="px-3 py-1.5 text-ink-dim font-mono whitespace-nowrap">{fmtTime(r.ts)}</td>
                  <td className="px-3 py-1.5 font-mono text-ink break-all">
                    <span className="text-ink-faint">{r.system}/</span>{r.method}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-ink-dim">{r.network}</td>
                  <td className="px-3 py-1.5"><OutcomeChip outcome={r.outcome} /></td>
                  <td className="px-3 py-1.5 text-right text-ink font-mono">{formatCc(BigInt(r.cc_charged))}</td>
                  <td className="px-3 py-1.5 text-right text-ink-dim font-mono">{r.duration_ms}ms</td>
                  <td className="px-3 py-1.5 font-mono text-ink-faint">{r.token_id}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="border-t border-white/5 px-3 py-2 flex items-center justify-between text-xs">
            <span className="text-ink-faint">
              {filter === 'errors'
                ? `${rows!.length} error/reject loaded`
                : `${rows!.length} most recent`}
            </span>
            {nextBefore && (
              <button onClick={loadMore} disabled={loading} className="btn-secondary text-xs disabled:opacity-50">
                {loading ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function FilterPill({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 text-xs rounded-md ring-1 ring-inset transition ${
        active
          ? 'bg-accent/15 text-accent ring-accent/30'
          : 'text-ink-dim ring-white/10 hover:bg-surface-2'
      }`}
    >
      {children}
    </button>
  );
}

function OutcomeChip({ outcome }: { outcome: string }) {
  // Bucket the gateway's verbose outcome vocabulary into three colors:
  // green for billable success, yellow for cached (still success but
  // free), red for anything the gateway refused / failed.
  const [head, tail] = outcome.split(':', 2) as [string, string | undefined];
  const tone = head === 'executed' ? 'bg-emerald-500/15 text-emerald-300'
             : head === 'cached'   ? 'bg-amber-500/15 text-amber-300'
             :                       'bg-red-500/15 text-red-300';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-mono ${tone}`}>
      {tail ? `${head}:${tail}` : head}
    </span>
  );
}

function fmtTime(iso: string): string {
  // For activity rows, time-of-day at second resolution is more useful
  // than month/day — most rows arrive within a single browsing session
  // and the calendar context comes from "Recent activity" framing.
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/** Generate a CSV from the loaded ActivityRow buffer and trigger
 *  a browser download. RFC-4180 quoting: wrap any cell that has a
 *  comma/newline/quote, double-up internal quotes. UTF-8 BOM so
 *  Excel's default Western locale opens it correctly without the
 *  user picking an import wizard. */
function downloadActivityCsv(rows: ActivityRow[]): void {
  if (rows.length === 0) return;
  const HEADERS = [
    'id', 'ts', 'system', 'network', 'method', 'token_id',
    'cc_charged', 'outcome', 'duration_ms', 'req_bytes', 'resp_bytes',
  ] as const;
  const quote = (v: string | number): string => {
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [HEADERS.join(',')];
  for (const r of rows) {
    lines.push([
      r.id, r.ts, r.system, r.network, r.method, r.token_id,
      r.cc_charged, r.outcome, r.duration_ms, r.req_bytes, r.resp_bytes,
    ].map(quote).join(','));
  }
  const body = '﻿' + lines.join('\n') + '\n';
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  const ts   = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  a.href     = url;
  a.download = `solidpeer-activity-${ts}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ============================================================
// Notify prefs editor
// ============================================================

function NotifyPrefsCard({ identity, account, prefs, busy, onSave, onAccountRefresh }: {
  identity: Identity;
  account:  AccountDto | null;
  prefs:    NotifyPrefs | null | undefined;
  busy:     boolean;
  onSave:   (next: NotifyPrefs | null) => void;
  onAccountRefresh: () => void;
}) {
  const [email, setEmail] = useState('');
  const [dirty, setDirty] = useState(false);

  // Hydrate inputs from server state once it lands; subsequent local
  // edits (`dirty=true`) take precedence to avoid clobbering keystrokes.
  useEffect(() => {
    if (dirty) return;
    setEmail(prefs?.email ?? '');
  }, [prefs, dirty]);

  function save() {
    const next: NotifyPrefs = {};
    if (email.trim())   next.email    = email.trim();
    // Telegram lives in the dedicated linked-chat columns now (not
    // notify_prefs), but if the operator previously typed a freeform
    // handle into notify_prefs.telegram we preserve it as a display
    // label — won't drop it on a save here.
    if (prefs?.telegram) next.telegram = prefs.telegram;
    if (prefs?.browser_pushsub) next.browser_pushsub = prefs.browser_pushsub;
    onSave(Object.keys(next).length === 0 ? null : next);
    setDirty(false);
  }

  function clearAll() {
    setEmail(''); setDirty(false);
    onSave(null);
  }

  return (
    <section className="card">
      <p className="text-sm text-ink-dim max-w-2xl">
        Optional. Where to reach you for payment receipts, low-balance warnings, and
        cycle-renewal reminders. None set today means we won't send anything; the dashboard
        is still the source of truth either way.
      </p>

      <div className="mt-6 grid gap-4 max-w-md">
        <Field label="Email" hint="ex: ops@example.com (placeholder — sender not yet wired)">
          <input
            type="email"
            value={email}
            onChange={(e) => { setDirty(true); setEmail(e.target.value); }}
            placeholder="ops@example.com"
            className="w-full rounded-md bg-surface-2 ring-1 ring-inset ring-white/5 px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-accent/50"
          />
        </Field>

        <Field label="Telegram" hint="Click Connect, open the link in Telegram, send /start.">
          <TelegramConnector
            identity={identity}
            account={account}
            onRefresh={onAccountRefresh}
          />
        </Field>

        <Field label="Browser push" hint="ServiceWorker push (RFC 8030). Requires HTTPS; first subscribe prompts for permission.">
          <BrowserPushConnector
            identity={identity}
            prefs={prefs}
            onRefresh={onAccountRefresh}
          />
        </Field>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <button onClick={save} disabled={busy || !dirty} className="btn-primary text-sm disabled:opacity-50">
          Save preferences
        </button>
        {prefs?.email && (
          <button onClick={clearAll} disabled={busy} className="btn-secondary text-sm disabled:opacity-50">
            Clear email
          </button>
        )}
      </div>
    </section>
  );
}

// ============================================================
// Telegram connector — handles the deep-link mint, polls for /start
// completion, and offers Test/Disconnect once linked.
// ============================================================

function TelegramConnector({ identity, account, onRefresh }: {
  identity:  Identity;
  account:   AccountDto | null;
  onRefresh: () => void;
}) {
  const [pending, setPending] = useState<{ deep_link: string; expires_at: string } | null>(null);
  const [busy,    setBusy]    = useState<'connect' | 'test' | 'disconnect' | null>(null);
  const [msg,     setMsg]     = useState<string | null>(null);
  const [err,     setErr]     = useState<string | null>(null);

  // While the connect modal is open, poll /account every 4s for a flip
  // to telegram_linked === true. Stop on link, on dismiss, or on expiry.
  useEffect(() => {
    if (!pending) return;
    const expires = Date.parse(pending.expires_at);
    let cancelled = false;
    const tick = setInterval(() => {
      if (cancelled) return;
      if (Date.now() > expires) {
        setPending(null);
        setErr('Link expired without /start. Try Connect again.');
        return;
      }
      onRefresh();
    }, 4000);
    return () => { cancelled = true; clearInterval(tick); };
  }, [pending, onRefresh]);

  // Server flipped telegram_linked → drop the modal.
  useEffect(() => {
    if (pending && account?.telegram_linked) {
      setPending(null);
      setMsg(`Connected${account.telegram_username ? ` as @${account.telegram_username}` : ''}.`);
    }
  }, [account, pending]);

  async function startLink() {
    setBusy('connect'); setErr(null); setMsg(null);
    try {
      const r = await linkTelegram(identity);
      setPending({ deep_link: r.deep_link, expires_at: r.expires_at });
    } catch (e) {
      console.error('linkTelegram failed', e);
      const eb = e as { status?: number; body?: { reason?: string } };
      if (eb.body?.reason === 'telegram_not_configured') {
        setErr("This rig hasn't been configured with a Telegram bot yet.");
      } else {
        setErr(e instanceof ApiCallFailed ? e.message : 'failed to mint link');
      }
    } finally { setBusy(null); }
  }

  async function sendTest() {
    setBusy('test'); setErr(null); setMsg(null);
    try {
      const r = await testTelegram(identity);
      setMsg(r.sent ? 'Test sent.' : 'Send failed — check Telegram.');
    } catch (e) {
      console.error('testTelegram failed', e);
      setErr(e instanceof ApiCallFailed ? e.message : 'test failed');
    } finally { setBusy(null); }
  }

  async function disconnect() {
    setBusy('disconnect'); setErr(null); setMsg(null);
    try {
      await disconnectTelegram(identity);
      onRefresh();
      setMsg('Disconnected.');
    } catch (e) {
      console.error('disconnectTelegram failed', e);
      setErr(e instanceof ApiCallFailed ? e.message : 'disconnect failed');
    } finally { setBusy(null); }
  }

  // Linked state — show username + Test + Disconnect
  if (account?.telegram_linked) {
    return (
      <div className="space-y-2">
        <div className="text-sm text-ink">
          Connected{account.telegram_username ? <> as <span className="font-mono text-accent">@{account.telegram_username}</span></> : null}.
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={sendTest} disabled={busy !== null}
                  className="btn-secondary text-xs disabled:opacity-50">
            {busy === 'test' ? 'Sending…' : 'Send test'}
          </button>
          <button onClick={disconnect} disabled={busy !== null}
                  className="btn-secondary text-xs disabled:opacity-50">
            {busy === 'disconnect' ? 'Disconnecting…' : 'Disconnect'}
          </button>
        </div>
        {msg && <div className="text-xs text-emerald-300">{msg}</div>}
        {err && <div className="text-xs text-red-300">{err}</div>}
      </div>
    );
  }

  // Pending — connect was clicked, waiting for /start
  if (pending) {
    return (
      <div className="rounded-md ring-1 ring-inset ring-accent/20 bg-accent/5 p-3 space-y-2">
        <div className="text-xs text-ink-dim">
          1. Open the link below in Telegram. 2. Tap Start. 3. Come back here — we'll detect it automatically.
        </div>
        <a href={pending.deep_link}
           target="_blank" rel="noopener noreferrer"
           className="btn-primary text-sm w-full text-center">
          Open in Telegram
        </a>
        <div className="text-xs text-ink-faint break-all font-mono">{pending.deep_link}</div>
        <button onClick={() => setPending(null)} className="btn-secondary text-xs">
          Cancel
        </button>
      </div>
    );
  }

  // Default — Connect button
  return (
    <div className="space-y-2">
      <button onClick={startLink} disabled={busy !== null}
              className="btn-primary text-sm w-full disabled:opacity-50">
        {busy === 'connect' ? 'Preparing link…' : 'Connect Telegram'}
      </button>
      {msg && <div className="text-xs text-emerald-300">{msg}</div>}
      {err && <div className="text-xs text-red-300">{err}</div>}
    </div>
  );
}

// ============================================================
// Browser push connector — subscribes to web push via the SW,
// PATCHes the resulting PushSubscription into notify_prefs.browser_pushsub.
// ============================================================

function BrowserPushConnector({ identity, prefs, onRefresh }: {
  identity:  Identity;
  prefs:     NotifyPrefs | null | undefined;
  onRefresh: () => void;
}) {
  const [supported, setSupported]   = useState<boolean | null>(null);
  const [vapidKey,  setVapidKey]    = useState<string | null>(null);
  const [busy,      setBusy]        = useState<'sub' | 'unsub' | null>(null);
  const [err,       setErr]         = useState<string | null>(null);
  const [permission, setPermission] = useState<NotificationPermission | 'unknown'>('unknown');

  const subscribed = !!prefs?.browser_pushsub;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const ok = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setSupported(ok);
    if (ok) setPermission(Notification.permission);
    if (!ok) return;

    // Fetch VAPID public key. 503 means operator hasn't configured it
    // yet — render a hint instead of the Subscribe button.
    getWebPushConfig()
      .then((c) => setVapidKey(c.vapid_public_key))
      .catch((e) => {
        console.error('getWebPushConfig failed', e);
        const eb = e as { status?: number; body?: { reason?: string } };
        if (eb.status === 503 && eb.body?.reason === 'web_push_not_configured') {
          setVapidKey(null);
        } else {
          setErr('Could not fetch web-push config from server.');
        }
      });
  }, []);

  async function subscribe() {
    if (!vapidKey) return;
    setBusy('sub'); setErr(null);
    try {
      // Permission first — a denied user has explicitly opted out;
      // PushManager.subscribe would just throw otherwise.
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setErr(perm === 'denied'
          ? 'Browser blocked notifications. Re-enable in site settings.'
          : 'Notification permission not granted.');
        return;
      }
      const reg = await navigator.serviceWorker.register('/sw.js');
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        // Modern PushManager accepts the base64url string directly —
        // the Uint8Array path was the older shape and trips TS over
        // SharedArrayBuffer-vs-ArrayBuffer variance.
        applicationServerKey: vapidKey,
      });
      const subJson = sub.toJSON();
      // Merge with any existing prefs — don't drop email/telegram.
      const next: NotifyPrefs = { ...(prefs ?? {}), browser_pushsub: subJson as Record<string, unknown> };
      await updateNotifyPrefs(identity, next as Record<string, unknown>);
      onRefresh();
    } catch (e) {
      console.error('push subscribe failed', e);
      setErr(e instanceof Error ? e.message : 'subscribe failed');
    } finally { setBusy(null); }
  }

  async function unsubscribe() {
    setBusy('unsub'); setErr(null);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
      // Drop browser_pushsub but keep the rest.
      const next: NotifyPrefs = { ...(prefs ?? {}) };
      delete next.browser_pushsub;
      await updateNotifyPrefs(identity, Object.keys(next).length === 0 ? null : (next as Record<string, unknown>));
      onRefresh();
    } catch (e) {
      console.error('push unsubscribe failed', e);
      setErr(e instanceof Error ? e.message : 'unsubscribe failed');
    } finally { setBusy(null); }
  }

  if (supported === null) {
    return <div className="text-xs text-ink-faint">Checking browser support…</div>;
  }
  if (!supported) {
    return <div className="text-xs text-ink-faint">This browser doesn't support web push.</div>;
  }
  if (vapidKey === null && err === null) {
    return <div className="text-xs text-ink-faint">This rig hasn't enabled web push yet.</div>;
  }

  if (subscribed) {
    return (
      <div className="space-y-2">
        <div className="text-sm text-ink">Subscribed on this device.</div>
        <button onClick={unsubscribe} disabled={busy !== null}
                className="btn-secondary text-xs disabled:opacity-50">
          {busy === 'unsub' ? 'Unsubscribing…' : 'Unsubscribe'}
        </button>
        {err && <div className="text-xs text-red-300">{err}</div>}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <button onClick={subscribe} disabled={busy !== null || !vapidKey}
              className="btn-primary text-sm w-full disabled:opacity-50">
        {busy === 'sub' ? 'Subscribing…' : 'Subscribe to web push'}
      </button>
      {permission === 'denied' && (
        <div className="text-xs text-amber-300">
          Notifications are blocked for this site. Re-enable in browser site settings.
        </div>
      )}
      {err && <div className="text-xs text-red-300">{err}</div>}
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-ink-faint">{label}</div>
      {hint && <div className="text-xs text-ink-faint mt-0.5">{hint}</div>}
      <div className="mt-2">{children}</div>
    </label>
  );
}

// ============================================================
// Recovery-key card
// ============================================================

function RecoveryKeyCard({ identity }: { identity: Identity }) {
  const [revealed, setRevealed] = useState(false);

  function downloadBackup() {
    // Plain text — easy to print, eyeball-verify, or store in a
    // password manager. We deliberately don't encrypt the file: the
    // customer is in control of where it lands; the WIF is what
    // they'd need to re-import anyway, and adding our own crypto
    // layer would just be another thing to lose the password to.
    const ts   = new Date().toISOString().replace(/[:.]/g, '-');
    const body = [
      '# SolidPeer recovery key',
      `# Generated: ${new Date().toISOString()}`,
      `# Account ID (public): ${identity.account_id}`,
      '#',
      '# The line below is your private key in WIF format.',
      '# Anyone with this string controls the account and balance.',
      '# Store offline. Do not share. We cannot recover it.',
      '',
      identity.wif,
      '',
    ].join('\n');
    const blob = new Blob([body], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `solidpeer-recovery-${shortAccountId(identity.account_id)}-${ts}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <section className="card">
      <h3 className="text-lg font-semibold">Recovery key</h3>
      <p className="mt-2 text-sm text-ink-dim">
        Your private key lives only in this browser. Wipe the storage / lose the device →
        lose the account. Re-export your WIF here as often as you like — copying it doesn't
        weaken it, but pasting it where someone else can see it does.
      </p>

      {!revealed && (
        <button onClick={() => setRevealed(true)} className="btn-secondary text-sm mt-4">
          Reveal recovery key
        </button>
      )}

      {revealed && (
        <div className="mt-4 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-faint">
              Account ID (public)
            </div>
            <code className="block mt-1 font-mono text-xs text-accent break-all">
              {identity.account_id}
            </code>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-ink-faint">
                Private key (WIF)
              </div>
              <CopyButton text={identity.wif} label="Copy" />
            </div>
            <code className="block mt-1 font-mono text-xs text-ink break-all bg-surface-2 ring-1 ring-inset ring-white/5 rounded p-3">
              {identity.wif}
            </code>
          </div>

          <div className="flex flex-wrap gap-3">
            <button onClick={downloadBackup} className="btn-secondary text-sm">
              Download as text file
            </button>
            <button onClick={() => setRevealed(false)} className="btn-secondary text-sm">
              Hide
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// ============================================================
// Token-mint form
// ============================================================

function TokenMintForm({ busy, account, onSubmit, onCancel }: {
  busy:     boolean;
  account:  AccountDto;
  onSubmit: (scope: TokenScope) => Promise<void> | void;
  onCancel: () => void;
}) {
  const [systems,  setSystems]  = useState<string[]>([...ALL_SYSTEMS]);
  const [networks, setNetworks] = useState<string[]>(['mainnet']);
  const [methods,  setMethods]  = useState('');     // comma-separated
  const [origins,  setOrigins]  = useState('');     // comma-separated
  const [expiresAt, setExpiresAt] = useState('');   // <input type="datetime-local"> value
  const [rpsCap,    setRpsCap]    = useState('');   // empty = no per-token RPS cap
  const [budget,    setBudget]    = useState('');   // empty = no per-token CC budget cap
  const [localError, setLocalError] = useState<string | null>(null);

  // Tier-derived ceilings — a per-token RPS cap above the account's tier
  // RPS is meaningless (the account-level bucket would reject anyway) and
  // a CC budget above the cycle grant is misleading (the account balance
  // is the harder ceiling). Both surface as input-validation hints + a
  // submit-time block so the form fails fast before the server roundtrip.
  const tierDef = TIERS[account.tier as TierName] ?? null;
  const term    = (account.subscription_term as SubscriptionTerm | null) ?? 'monthly';
  const maxRps  = tierDef?.rps_cap          ?? null;
  const maxBudget = tierDef
    ? bundleGrantCc(account.tier as TierName, term)
    : null;

  function toggle<T extends string>(arr: string[], setter: (v: string[]) => void, value: T) {
    setter(arr.includes(value) ? arr.filter(x => x !== value) : [...arr, value]);
  }

  function parseCsv(s: string): string[] {
    return s.split(',').map(x => x.trim()).filter(Boolean);
  }

  function submit() {
    if (systems.length === 0) {
      setLocalError('Pick at least one system.');
      return;
    }
    if (networks.length === 0) {
      setLocalError('Pick at least one network.');
      return;
    }
    setLocalError(null);

    let expires_iso = '';
    if (expiresAt) {
      // <input type="datetime-local"> emits "YYYY-MM-DDTHH:MM" (no zone).
      // Treat as local time, convert to ISO with the browser's offset.
      const ms = Date.parse(expiresAt);
      if (Number.isNaN(ms)) {
        setLocalError('Expiry timestamp is unparseable.');
        return;
      }
      expires_iso = new Date(ms).toISOString();
    }

    // Optional caps. Empty inputs become null (no cap); positive
    // integers pass through to the server. Negatives / non-numeric
    // → form-level error so we don't silently coerce.
    const rps_cap_n         = rpsCap === '' ? null : parseInt(rpsCap, 10);
    const spend_budget_cc_n = budget === '' ? null : parseInt(budget, 10);
    if (rps_cap_n         !== null && (!Number.isFinite(rps_cap_n)         || rps_cap_n         <= 0)) {
      setLocalError('RPS cap must be a positive integer (or empty for no cap).');
      return;
    }
    if (spend_budget_cc_n !== null && (!Number.isFinite(spend_budget_cc_n) || spend_budget_cc_n <= 0)) {
      setLocalError('Budget must be a positive integer (or empty for no cap).');
      return;
    }
    // Tier-ceiling enforcement. The server enforces these too — this
    // catches the obvious mistakes before round-trip and gives a more
    // specific error message than a 400 on POST /token.
    if (maxRps !== null && rps_cap_n !== null && rps_cap_n > maxRps) {
      setLocalError(`RPS cap can't exceed your ${account.tier} tier's ${maxRps.toLocaleString()} cap.`);
      return;
    }
    if (maxBudget !== null && spend_budget_cc_n !== null && spend_budget_cc_n > maxBudget) {
      setLocalError(`CC budget can't exceed your ${account.tier} tier's ${formatCc(BigInt(maxBudget))} cycle grant.`);
      return;
    }

    void onSubmit({
      allowed_systems:  systems,
      allowed_networks: networks,
      allowed_methods:  parseCsv(methods),
      allowed_origins:  parseCsv(origins),
      expires_at:       expires_iso,
      rps_cap:          rps_cap_n,
      spend_budget_cc:  spend_budget_cc_n,
    });
  }

  return (
    <div className="card space-y-6">
      <div>
        <h3 className="text-base font-semibold">New token scope</h3>
        <p className="mt-1 text-xs text-ink-dim">
          Tighter scope = smaller blast radius if the token leaks. Browser-deployed credentials
          should pin <span className="font-mono">allowed_origins</span> to your hostname.
        </p>
      </div>

      <Field label="Systems" hint="Required. Which protocols this token can call.">
        <div className="flex flex-wrap gap-3">
          {ALL_SYSTEMS.map((s) => (
            <CheckPill key={s} label={s} checked={systems.includes(s)}
                       onToggle={() => toggle(systems, setSystems, s)} />
          ))}
        </div>
      </Field>

      <Field label="Networks" hint="Required. Mainnet costs 1× CC, testnets/regtest 0.5×.">
        <div className="flex flex-wrap gap-3">
          {ALL_NETWORKS.map((n) => (
            <CheckPill key={n} label={n} checked={networks.includes(n)}
                       onToggle={() => toggle(networks, setNetworks, n)} />
          ))}
        </div>
      </Field>

      <Field label="Allowed methods (optional, comma-separated)"
             hint="Leave empty for the default read-only allowlist. Set to scope a token to specific RPC methods (e.g. getblockcount, getrawtransaction).">
        <textarea
          value={methods}
          onChange={(e) => setMethods(e.target.value)}
          placeholder="getblockcount, getrawtransaction"
          rows={2}
          className="w-full rounded-md bg-surface-2 ring-1 ring-inset ring-white/5 px-3 py-2 font-mono text-xs text-ink placeholder:text-ink-faint focus:outline-none focus:ring-accent/50"
        />
      </Field>

      <Field label="Allowed origins (optional, comma-separated)"
             hint="Hostname allowlist for browser-deployed credentials. Server-to-server clients should leave this empty.">
        <textarea
          value={origins}
          onChange={(e) => setOrigins(e.target.value)}
          placeholder="app.example.com"
          rows={2}
          className="w-full rounded-md bg-surface-2 ring-1 ring-inset ring-white/5 px-3 py-2 font-mono text-xs text-ink placeholder:text-ink-faint focus:outline-none focus:ring-accent/50"
        />
      </Field>

      <Field label="Expires at (optional)"
             hint="Local time; converted to UTC. Default: no expiry.">
        <input
          type="datetime-local"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          className="w-full rounded-md bg-surface-2 ring-1 ring-inset ring-white/5 px-3 py-2 text-sm text-ink focus:outline-none focus:ring-accent/50"
        />
      </Field>

      <Field label="Per-token RPS cap (optional)"
             hint={maxRps !== null
               ? `Caps THIS token's sustained rate. Max ${maxRps.toLocaleString()} (your ${account.tier} tier ceiling). Empty = inherit account-level cap.`
               : `Caps THIS token's sustained rate. Empty = inherit account-level cap.`}>
        <input
          type="number"
          min={1}
          step={1}
          {...(maxRps !== null ? { max: maxRps } : {})}
          value={rpsCap}
          onChange={(e) => setRpsCap(e.target.value)}
          placeholder={maxRps !== null ? `e.g. 50 (max ${maxRps.toLocaleString()})` : 'e.g. 50'}
          className="w-full rounded-md bg-surface-2 ring-1 ring-inset ring-white/5 px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-accent/50"
        />
      </Field>

      <Field label="Per-token CC budget (optional)"
             hint={maxBudget !== null
               ? `One-time spend cap. Max ${formatCc(BigInt(maxBudget))} (your ${account.tier} ${term} cycle grant). Empty = no cap.`
               : `One-time spend cap. Once this token has consumed this many CC, it stops working — independent of your account balance. Empty = no cap.`}>
        <input
          type="number"
          min={1}
          step={1}
          {...(maxBudget !== null ? { max: maxBudget } : {})}
          value={budget}
          onChange={(e) => setBudget(e.target.value)}
          placeholder={maxBudget !== null ? `e.g. 1000000 (max ${maxBudget.toLocaleString()})` : 'e.g. 1000000 (1M CC)'}
          className="w-full rounded-md bg-surface-2 ring-1 ring-inset ring-white/5 px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-accent/50"
        />
      </Field>

      {localError && (
        <div className="text-sm text-red-300">{localError}</div>
      )}

      <div className="flex flex-wrap gap-3">
        <button onClick={submit} disabled={busy} className="btn-primary text-sm disabled:opacity-50">
          Issue token
        </button>
        <button onClick={onCancel} disabled={busy} className="btn-secondary text-sm disabled:opacity-50">
          Cancel
        </button>
      </div>
    </div>
  );
}

function CheckPill({ label, checked, onToggle }: { label: string; checked: boolean; onToggle: () => void }) {
  return (
    <label className={`pill cursor-pointer select-none transition ${
      checked ? 'bg-accent/15 text-accent' : 'hover:bg-surface-3'
    }`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="sr-only"
      />
      <span className="font-mono">{label}</span>
    </label>
  );
}

// ============================================================
// Helpers
// ============================================================

function asMessage(err: unknown): string {
  if (err instanceof ApiCallFailed) {
    const reason = ('reason' in err.body && err.body.reason) ? ` (${err.body.reason})` : '';
    return `${err.status}: ${'error' in err.body ? err.body.error : 'request failed'}${reason}`;
  }
  return (err as Error).message;
}
